import { useState, useEffect } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import styles from "./AddFareOptionModal.module.css";

const BAGGAGE_TYPES = [
  { key: "handbag", label: "Handbag / Personal Item", apiValue: "Handbag", icon: "👜" },
  { key: "cabinBaggage", label: "Cabin Baggage", apiValue: "Cabin Baggage", icon: "🎒" },
  { key: "checkedBaggage", label: "Checked Baggage", apiValue: "Checked Baggage", icon: "🧳" },
];

const emptyBaggage = {
  included: false,
  numberOfPieces: 1,
  weightPerPiece: 0,
  size: "",
};

const emptyForm = {
  fareClassId: "",
  handbag: { ...emptyBaggage, numberOfPieces: 1, weightPerPiece: 2 },
  cabinBaggage: { ...emptyBaggage, numberOfPieces: 1, weightPerPiece: 7 },
  checkedBaggage: { ...emptyBaggage, numberOfPieces: 1, weightPerPiece: 23 },
  disclaimerText: "",
};

const toFormBaggage = (fo) =>
  fo
    ? {
        included: Boolean(fo.included),
        numberOfPieces: fo.numberOfPieces ?? 1,
        weightPerPiece: fo.weightPerPiece ?? 0,
        size: fo.size ?? "",
      }
    : { ...emptyBaggage };

const toApiPayload = (baggage, baggageType) => ({
  baggageType,
  included: baggage.included,
  numberOfPieces: baggage.included ? baggage.numberOfPieces : null,
  weightPerPiece: baggage.included ? baggage.weightPerPiece : null,
  size: baggage.size?.trim() || null,
  disclaimerText: null,
});

const AddFareOptionModal = ({ open, onClose, onSave, fareOptionGroup, fareClasses = [], apiError }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (fareOptionGroup) {
      setForm({
        fareClassId: String(fareOptionGroup.fareClassId),
        handbag: toFormBaggage(fareOptionGroup.handbag),
        cabinBaggage: toFormBaggage(fareOptionGroup.cabinBaggage),
        checkedBaggage: toFormBaggage(fareOptionGroup.checkedBaggage),
        disclaimerText: fareOptionGroup.handbag?.disclaimerText || fareOptionGroup.cabinBaggage?.disclaimerText || fareOptionGroup.checkedBaggage?.disclaimerText || "",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [fareOptionGroup, open]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleBaggageChange = (baggageKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      [baggageKey]: {
        ...prev[baggageKey],
        [field]: value,
      },
    }));
  };

  const validateAllFields = () => {
    const newErrors = {};
    if (!fareOptionGroup && !form.fareClassId) {
      newErrors.fareClassId = "Fare class is required";
    }
    const hasAnyIncluded =
      form.handbag.included || form.cabinBaggage.included || form.checkedBaggage.included;
    if (!hasAnyIncluded) {
      newErrors.general = "At least one baggage type must be included";
    }
    if (form.checkedBaggage.included && (!form.checkedBaggage.weightPerPiece || form.checkedBaggage.weightPerPiece <= 0)) {
      newErrors.checkedWeight = "Weight per piece is required when checked baggage is included";
    }
    if (form.cabinBaggage.included && (!form.cabinBaggage.weightPerPiece || form.cabinBaggage.weightPerPiece <= 0)) {
      newErrors.cabinWeight = "Weight per piece is required when cabin baggage is included";
    }
    if (form.handbag.included && (!form.handbag.weightPerPiece || form.handbag.weightPerPiece <= 0)) {
      newErrors.handbagWeight = "Weight per piece is required when handbag is included";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setForm(emptyForm);
    setErrors({});
    onClose();
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!validateAllFields()) return;

    const disclaimerText = form.disclaimerText?.trim() || null;

    let payload;
    if (fareOptionGroup) {
      payload = {
        fareClassId: fareOptionGroup.fareClassId,
        handbag: form.handbag.included
          ? { ...toApiPayload(form.handbag, "Handbag"), disclaimerText, fareClassId: fareOptionGroup.fareClassId, id: fareOptionGroup.handbag?.id }
          : null,
        cabinBaggage: form.cabinBaggage.included
          ? { ...toApiPayload(form.cabinBaggage, "Cabin Baggage"), disclaimerText, fareClassId: fareOptionGroup.fareClassId, id: fareOptionGroup.cabinBaggage?.id }
          : null,
        checkedBaggage: form.checkedBaggage.included
          ? { ...toApiPayload(form.checkedBaggage, "Checked Baggage"), disclaimerText, fareClassId: fareOptionGroup.fareClassId, id: fareOptionGroup.checkedBaggage?.id }
          : null,
      };
    } else {
      payload = {
        fareClassId: parseInt(form.fareClassId, 10),
        handbag: form.handbag.included
          ? { ...toApiPayload(form.handbag, "Handbag"), disclaimerText }
          : null,
        cabinBaggage: form.cabinBaggage.included
          ? { ...toApiPayload(form.cabinBaggage, "Cabin Baggage"), disclaimerText }
          : null,
        checkedBaggage: form.checkedBaggage.included
          ? { ...toApiPayload(form.checkedBaggage, "Checked Baggage"), disclaimerText }
          : null,
      };
    }

    setSaving(true);
    try {
      const errorMessage = await onSave(payload);
      if (!errorMessage) {
        handleClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some((e) => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={fareOptionGroup ? "Edit Fare Option" : "Add New Fare Option"}
      width="720px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose} disabled={saving}>
            ✕ Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </>
      }
    >
      <div className={styles.iconHeader}>
        <div className={styles.icon}>💼</div>
        <div className={styles.headerText}>
          <h3>{fareOptionGroup ? "Edit Fare Option" : "Add New Fare Option"}</h3>
          <p>
            Configure baggage allowances and inclusions for a fare class. These settings are fixed and cannot be modified per booking.
          </p>
        </div>
      </div>

      {apiError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Error:</strong> {apiError}
          </div>
        </div>
      )}

      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, err]) => err && <li key={key}>{err}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* Fare Class Selection (add mode only) */}
      {!fareOptionGroup && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>🎫 FARE CLASS</h4>
          <div className={styles.fieldWide}>
            <label>SELECT FARE CLASS <span className={styles.required}>*</span></label>
            <select
              value={form.fareClassId}
              onChange={(e) => handleChange("fareClassId", e.target.value)}
              className={errors.fareClassId ? styles.inputError : ""}
            >
              <option value="">Select fare class</option>
              {fareClasses.map((fc) => (
                <option key={fc.id} value={fc.id}>
                  {fc.fareClassName} ({fc.fareClassCode}) - {fc.airline}
                </option>
              ))}
            </select>
            {errors.fareClassId && (
              <span className={styles.errorText}>{errors.fareClassId}</span>
            )}
            {!fareOptionGroup && fareClasses.length === 0 && (
              <span className={styles.helpText} style={{ color: "#b45309" }}>
                No fare classes found. Create fare classes first from the Fare Classes page.
              </span>
            )}
          </div>
        </div>
      )}

      {fareOptionGroup && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>🎫 FARE CLASS</h4>
          <div className={styles.fieldWide}>
            <strong>{fareOptionGroup.fareClassName}</strong> ({fareOptionGroup.fareClassCode}) - {fareOptionGroup.airlineName}
          </div>
        </div>
      )}

      {/* Baggage Configuration */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>✈️ BAGGAGE ALLOWANCE</h4>

        {BAGGAGE_TYPES.map(({ key, label, icon }) => {
          const bag = form[key];
          return (
            <div key={key} className={styles.baggageCard}>
              <div className={styles.baggageCardHeader}>
                <div className={styles.baggageIcon}>{icon}</div>
                <div className={styles.baggageTitle}>
                  <h5>{label}</h5>
                </div>
                <div className={styles.toggleField}>
                  <input
                    type="checkbox"
                    id={key}
                    checked={bag.included}
                    onChange={(e) => handleBaggageChange(key, "included", e.target.checked)}
                    className={styles.toggleInput}
                  />
                  <label htmlFor={key} className={styles.toggleLabel}>
                    <span className={styles.toggleSwitch}></span>
                    <span className={styles.toggleText}>
                      {bag.included ? "Included" : "Not Included"}
                    </span>
                  </label>
                </div>
              </div>
              {bag.included && (
                <div className={styles.baggageFields}>
                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <label>NUMBER OF PIECES</label>
                      <select
                        value={bag.numberOfPieces}
                        onChange={(e) =>
                          handleBaggageChange(key, "numberOfPieces", parseInt(e.target.value, 10))
                        }
                      >
                        <option value={1}>1 piece</option>
                        <option value={2}>2 pieces</option>
                      </select>
                    </div>
                    <div className={styles.field}>
                      <label>WEIGHT PER PIECE (KG) <span className={styles.required}>*</span></label>
                      <input
                        type="number"
                        value={bag.weightPerPiece}
                        onChange={(e) =>
                          handleBaggageChange(key, "weightPerPiece", parseFloat(e.target.value) || 0)
                        }
                        placeholder={key === "handbag" ? "2" : key === "cabinBaggage" ? "7" : "23"}
                        min="0"
                        max={key === "handbag" ? 20 : key === "cabinBaggage" ? 50 : 100}
                        className={errors[`${key}Weight`] ? styles.inputError : ""}
                      />
                      {errors[`${key}Weight`] && (
                        <span className={styles.errorText}>{errors[`${key}Weight`]}</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>SIZE (OPTIONAL)</label>
                    <input
                      type="text"
                      value={bag.size}
                      onChange={(e) => handleBaggageChange(key, "size", e.target.value)}
                      placeholder="40cm × 30cm × 20cm"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>📄 DISCLAIMER TEXT</h4>
        <div className={styles.fieldWide}>
          <label>FARE OPTION DISCLAIMER</label>
          <textarea
            value={form.disclaimerText}
            onChange={(e) => handleChange("disclaimerText", e.target.value)}
            placeholder="Enter disclaimer text displayable on booking documents..."
            rows={4}
            className={styles.textarea}
          />
        </div>
      </div>

      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>Fare options are fixed per fare class and cannot be modified per booking</li>
            <li>At least one baggage type must be configured</li>
            <li>Weight per piece is required when baggage is included</li>
          </ul>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddFareOptionModal;
